import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AnalyticEvents } from '@app/enums';
import { InterviewQuestionType } from '@app/models/recruiting/interview-question-type.model';
import { InterviewQuestion } from '@app/models/recruiting/interview-question.model';
import { AnalyticService, NotifyService } from '@app/services';
import { InterviewQuestionDisplayOptions } from '@app/types/translatables/applicant-tracker/interview-question-display.options';
import { TranslateService } from '@ngx-translate/core';
import { ApplicantInterviewQuestionTypes } from '../../enums/applicant-interview-question-types.enum';
import { JobPostingInterviewMultipleChoiceTypes } from '../../enums/job-posting-interview-multiple-choice-types.enum';
import { JobPostingInterviewOpinionScaleTypes } from '../../enums/job-posting-interview-opinion-scale-types.enum';

@Component({
    selector: 'app-applicant-interview-question-dialog',
    templateUrl: './applicant-interview-question-dialog.html',
    styleUrls: ['./applicant-interview-question-dialog.style.scss'],
})
export class ApplicantInterviewQuestionDialogComponent implements OnInit {
    @ViewChild('form') form: NgForm;
    @Input() openDialog = false;
    @Input() editMode = false;
    @Input() interviewQuestion = new InterviewQuestion({ isRequired: true });
    @Output() addApplicantInterviewQuestion = new EventEmitter();
    @Output() closeApplicantInterviewQuestionDialog = new EventEmitter();

    isLoading = false;
    interviewQuestionTypes: InterviewQuestionType[] = [];
    selectedQuestionType = '';
    questionTypeMode: string = null;
    questionValues = null;
    applicantInterviewQuestionTypesEnum = ApplicantInterviewQuestionTypes;
    jobPostingInterviewOpinionScaleTypes = JobPostingInterviewOpinionScaleTypes;
    jobPostingInterviewMultipleChoiceTypes = JobPostingInterviewMultipleChoiceTypes;
    opinionScaleNumbers = [1, 2, 3, 4, 5];
    opinionScaleStars = Array(5).fill(0);

    interviewQuestionDisplay = InterviewQuestionDisplayOptions;

    constructor(
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private readonly translateService: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        try {
            const [interviewQuestionTypes] = await InterviewQuestionType.all();
            this.interviewQuestionTypes = interviewQuestionTypes;

            this.selectedQuestionType = this.interviewQuestion.interviewQuestionType?.name;
            this.questionTypeMode = this.interviewQuestion.questionTypeMode;
            this.questionValues = this.interviewQuestion.questionValues;
        } catch (err) {
            this.notify.error(err || 'applicant-tracker.couldNotFetchInterviewQuestionTypes');
        }

        this.isLoading = false;
    }

    onQuestionTypeSelected(): void {
        if (this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.multipleChoice) {
            this.questionValues = ['', ''];
            this.questionTypeMode = JobPostingInterviewMultipleChoiceTypes.singleSelect;
            return;
        }

        if (this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.openQuestion) {
            this.questionValues = null;
            return;
        }

        if (this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.opinionScale) {
            this.questionValues = [
                this.translateService.instant('applicant-tracker.weak'),
                '',
                this.translateService.instant('applicant-tracker.strong'),
            ];
            this.questionTypeMode = JobPostingInterviewOpinionScaleTypes.numerical;
            return;
        }

        if (this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.yesNo) {
            this.questionValues = null;
            return;
        }
    }

    onScaleTypeChange(): void {
        this.questionValues =
            this.questionTypeMode === this.jobPostingInterviewOpinionScaleTypes.numerical
                ? [
                      this.translateService.instant('applicant-tracker.weak'),
                      '',
                      this.translateService.instant('applicant-tracker.strong'),
                  ]
                : [
                      this.translateService.instant('applicant-tracker.tooGeneral'),
                      '',
                      this.translateService.instant('applicant-tracker.veryDetailed'),
                  ];
    }

    onCancel(): void {
        this.resetInterviewQuestion();

        this.closeApplicantInterviewQuestionDialog.emit();
    }

    onAddQuestion(form: NgForm): void {
        if (!form.valid) {
            return;
        }

        this.interviewQuestion.questionValues = this.questionValues;
        const interviewQuestionType = this.interviewQuestionTypes.find(
            (questionType) => questionType.name === this.selectedQuestionType
        );

        this.interviewQuestion.atsInterviewQuestionTypeId = interviewQuestionType?.id;
        this.interviewQuestion.interviewQuestionType = interviewQuestionType;
        this.interviewQuestion.questionTypeMode = this.questionTypeMode;

        this.addApplicantInterviewQuestion.emit(this.interviewQuestion);

        this.questionTypeAnalytics(interviewQuestionType.name);

        this.resetInterviewQuestion();
    }

    onAddQuestionValue(): void {
        if (this.questionValues.length >= 6) {
            return;
        }
        this.questionValues.push('');
    }

    onRemoveChoice(i: number): void {
        this.questionValues = this.questionValues.filter((_, index) => index !== i);
    }

    trackByQuestionValuesId(index: number, _: string): number {
        return index;
    }

    isAddQuestionsButtonDisabled(form: NgForm): boolean {
        if (!form.valid) {
            return true;
        }

        const isMultipleChoiceOrOpinionScaleQuestionType =
            this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.multipleChoice ||
            this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.opinionScale;

        if (!isMultipleChoiceOrOpinionScaleQuestionType) {
            return !this.interviewQuestion.question;
        }

        // If multiple choice we must fill all question values and if opinion scale we must fill 2 out the 3 fixed values
        const maxNumberOfQuestionValuesToBeFilled =
            this.selectedQuestionType === this.applicantInterviewQuestionTypesEnum.multipleChoice
                ? this.questionValues.length
                : this.questionValues.length - 1;

        const allQuestionValuesNotFilled =
            this.questionValues &&
            this.questionValues?.filter((value) => !!value)?.length < maxNumberOfQuestionValuesToBeFilled;

        const questionTypeModeRequired = isMultipleChoiceOrOpinionScaleQuestionType && !this.questionTypeMode;

        return !this.interviewQuestion.question || allQuestionValuesNotFilled || questionTypeModeRequired;
    }

    resetInterviewQuestion(): void {
        this.interviewQuestion = new InterviewQuestion({ isRequired: true });
        this.questionValues = null;
        this.questionTypeMode = null;
        this.selectedQuestionType = null;
    }

    getQuestionValueIndex(index: number): number {
        switch (index) {
            case 0:
                return 0;
            case 2:
                return 1;
            case 4:
                return 2;
        }
    }

    private questionTypeAnalytics(interviewQuestionType: string): void {
        switch (interviewQuestionType) {
            case ApplicantInterviewQuestionTypes.multipleChoice:
                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitngJobPostingInterviewsCreateInterviewAddQuestionMultipleChoice
                );
                break;

            case ApplicantInterviewQuestionTypes.openQuestion:
                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingJobPostingInterviewsCreateInteviewAddQuestionOpenQuestion
                );
                break;

            case ApplicantInterviewQuestionTypes.yesNo:
                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitngJobPostingInterviewsCreateInterviewAddQuestionYesNo
                );
                break;

            case ApplicantInterviewQuestionTypes.opinionScale:
                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingJobPostingInterviewsCreateInterviewAddQuestionOpnionScale
                );
                break;
        }
    }
}
